import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { VerifySignatureResponse } from '../models/verify-signature-response.model';

@Injectable({
  providedIn: 'root'
})
export class CertificateVerificationService {
  private verifyDocumentMultipartUrl =
    (environment as { verifyDocumentMultipartApiUrl?: string }).verifyDocumentMultipartApiUrl
      ?? `${environment.apiBaseUrl}/api/verify/document/multipart`;
  private downloadOriginalUrl =
    (environment as { verifyDownloadOriginalApiUrl?: string }).verifyDownloadOriginalApiUrl
      ?? `${environment.apiBaseUrl}/api/verify/download-original`;

  constructor(private http: HttpClient) {}

  /**
   * Verifica documento firmado (PDF o CAdES) vía multipart binario.
   * POST /api/verify/document/multipart — partes: document (+ fileName opcional).
   */
  verifySignature(file: File): Observable<VerifySignatureResponse> {
    const form = new FormData();
    form.append('document', file, file.name || 'documento');
    if (file.name?.trim()) {
      form.append('fileName', file.name.trim());
    }
    return this.http.post<VerifySignatureResponse>(this.verifyDocumentMultipartUrl, form);
  }

  /** @deprecated Usar {@link verifySignature}; el BFF detecta el tipo por extensión. */
  verifyPdf(file: File): Observable<VerifySignatureResponse> {
    return this.verifySignature(file);
  }

  /** @deprecated Usar {@link verifySignature}; el BFF detecta el tipo por extensión. */
  verifyCades(file: File): Observable<VerifySignatureResponse> {
    return this.verifySignature(file);
  }

  /**
   * Extrae el documento original embebido en CAdES attached.
   * POST /api/verify/download-original — partes: document (+ fileName opcional).
   */
  downloadOriginalFile(file: File): Observable<HttpResponse<Blob>> {
    const formData = new FormData();
    formData.append('document', file, file.name || 'contenedor.p7z');
    if (file.name?.trim()) {
      formData.append('fileName', file.name.trim());
    }

    return this.http.post(this.downloadOriginalUrl, formData, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(
      catchError((error: HttpErrorResponse) => this.handleDownloadError(error))
    );
  }

  private handleDownloadError(error: HttpErrorResponse): Observable<never> {
    console.error('Error al extraer contenido original:', error);
    const fallback = 'No se pudo extraer el contenido original del contenedor.';
    const body = error?.error;
    if (body instanceof Blob) {
      return new Observable((subscriber) => {
        body.text().then((text) => {
          try {
            const json = JSON.parse(text) as { message?: string };
            subscriber.error(new Error(json?.message || fallback));
          } catch {
            subscriber.error(new Error(fallback));
          }
        }).catch(() => subscriber.error(new Error(fallback)));
      });
    }
    const msg = (body as { message?: string })?.message;
    return throwError(() => new Error(msg || fallback));
  }
}
